import { defineStore } from 'pinia';
import { useGlobalStore } from '@/store/global-store-base.ts';
import { useApi } from '@lib/api.ts';
import type { BrandSupplierDto } from '@/ApiClient.ts';
import { computed, type ComputedRef } from 'vue';
import { useSupplierUsage } from '@/store/supplier-usage.ts';
import { useCountries } from '@/store/countries.ts';

export const useSuppliers = defineStore('suppliers', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.brandSupplier_BrandSuppliers,
    dto: 'BrandSupplierDto',
    create: api.brandSupplier_AddBrandSupplier,
    delete: api.brandSupplier_DeleteBrandSupplier,
    signalR: 'BrandSupplierUpdate',
  });

  const { loaded } = globalStoreBase;

  // Helper functions for sub-suppliers
  function getSubSuppliers(supplier: BrandSupplierDto): BrandSupplierDto[] {
    const suppliers = globalStoreBase.loaded.value || [];
    return supplier.subSupplierIds
      .map((id) => suppliers.find((s) => s.id === id))
      .filter((s): s is BrandSupplierDto => s !== undefined);
  }

  function getMainSuppliers(): BrandSupplierDto[] {
    const suppliers = globalStoreBase.loaded.value || [];
    const allSubSupplierIds = new Set(suppliers.flatMap((s) => s.subSupplierIds));
    return suppliers.filter((s) => !allSubSupplierIds.has(s.id));
  }

  function supplierMatchesSearch(supplier: BrandSupplierDto, searchTerm: string): boolean {
    if (!searchTerm.trim()) return true;

    const term = searchTerm.toLowerCase();

    if (supplier.country && useCountries().matches(supplier.country, term)) return true;

    const searchableFields = [
      supplier.name,
      supplier.supplierNumber,
      supplier.externalNumber,
      supplier.manufacturerId,
      supplier.oarId,
      supplier.contactEmail,
      supplier.internalComment,
      supplier.address,
      supplier.country?.codeAlpha3,
    ];

    return searchableFields.some((field) => {
      if (!field) return false;
      const fieldString = typeof field === 'string' ? field : String(field);
      return fieldString.toLowerCase().includes(term);
    });
  }

  // Enhanced search function for local supplier filtering with sub-supplier support
  function searchSuppliers(searchTerm?: string, excludeIds: number[] = []) {
    const mainSuppliers = getMainSuppliers();

    if (!searchTerm?.trim()) {
      return mainSuppliers.filter((s) => !excludeIds.includes(s.id) || getSubSuppliers(s).length > 0);
    }

    const results: BrandSupplierDto[] = [];

    mainSuppliers.forEach((supplier) => {
      const subSuppliers = getSubSuppliers(supplier);
      const hasSubSuppliers = subSuppliers.length > 0;

      // Only exclude if selected AND has no sub-suppliers
      if (excludeIds.includes(supplier.id) && !hasSubSuppliers) return;

      const mainMatches = supplierMatchesSearch(supplier, searchTerm);
      const matchingSubSuppliers = subSuppliers.filter(
        (sub) => !excludeIds.includes(sub.id) && supplierMatchesSearch(sub, searchTerm)
      );

      // Include supplier if main matches or has matching sub-suppliers
      if (mainMatches || matchingSubSuppliers.length > 0) {
        // If parent matches, include ALL sub-suppliers (not just matching ones)
        // If parent doesn't match, only include matching sub-suppliers
        const subsToInclude = mainMatches
          ? subSuppliers.filter((sub) => !excludeIds.includes(sub.id))
          : matchingSubSuppliers;

        results.push({
          ...supplier,
          _matchingSubSuppliers: subsToInclude,
          _shouldExpand: !mainMatches && matchingSubSuppliers.length > 0,
        } as BrandSupplierDto & { _matchingSubSuppliers: BrandSupplierDto[]; _shouldExpand: boolean });
      }
    });

    return results;
  }

  // Empty until useSupplierUsage().load() has answered; the pages owning a supplier filter call it
  // on mount.
  const productSuppliers: ComputedRef<BrandSupplierDto[]> = computed(() => {
    const ids = useSupplierUsage().onArticles;
    return loaded.value.filter((s) => ids.has(s.id));
  });

  const componentSuppliers: ComputedRef<BrandSupplierDto[]> = computed(() => {
    const ids = useSupplierUsage().onComponents;
    return loaded.value.filter((s) => ids.has(s.id));
  });

  return {
    ...globalStoreBase,
    loaded,
    productSuppliers,
    componentSuppliers,
    searchSuppliers,
    getSubSuppliers,
    getMainSuppliers,
    supplierMatchesSearch,
  };
});
